import { observer } from 'mobx-react';
import * as React from 'react';
import { SliderView, ISliderSettings } from 'elc-slider';
import styled, { css } from 'styled-components';
import { DEFAULT_THUMBNAILS_CAROUSEL_SETTINGS } from '../../constants/ImageThumbnailCarouselSettings';
import { ContentSpacing, Breakpoints } from 'elc-base-theme';
import { Desktop } from 'elc-layouts';
import { ProductImages } from 'elc-images';
import Slider from 'react-slick';
import { IProductImage, IProductFullCarouselConfig, IProductSlider } from './ProductFullCarousel';

export interface IProductThumbnailsCarousel {
    config: IProductFullCarouselConfig;
    sliderImages: IProductImage[];
    desktopCarouselSettings: ISliderSettings;
    isThumbnailsSliderVertical: boolean;
    sliderItems: JSX.Element[];
    carouselRef: Slider;
    thumbnailsCarouselRef: Slider;
    setCarouselRef(productImagesCarousel: Slider): Slider;
    setThumbnailsCarouselRef(thumbnailImagesCarousel: Slider): Slider;
}

const StyledProductSlider = styled.div`
    width: 50%;
    position: relative;
    margin: 0 auto;
`;

const StyledThumbnailsSlider = styled.div`
    height: 5%;
    ${({ isThumbnailsSliderVertical }: IProductSlider) =>
        isThumbnailsSliderVertical &&
        css`
            height: unset;
            width: 13%;
        `}
    margin: ${ContentSpacing.space16} 0;
`;

const ThumbnailImageWrapper = styled.div`
    padding: ${ContentSpacing.space4};
`;

@observer
export class ProductThumbnailsCarousel extends React.Component<IProductThumbnailsCarousel> {
    public onImageLoaded = () => {
        window.dispatchEvent(new Event('resize'));
    };

    public setInitialSlide(carouselRef: Slider, carouselSettings: ISliderSettings) {
        const { initialSlide } = carouselSettings;
        if (carouselRef && initialSlide !== undefined) {
            carouselRef.slickGoTo(initialSlide);
        }
    }

    public render() {
        const {
            sliderImages,
            desktopCarouselSettings,
            isThumbnailsSliderVertical,
            sliderItems,
            carouselRef,
            thumbnailsCarouselRef,
            setCarouselRef,
            setThumbnailsCarouselRef
        } = this.props;

        const { imageCarouselSettings } = this.props.config;

        const thumbnailsCarouselConfigSettings =
            imageCarouselSettings && imageCarouselSettings.thumbnailsCarouselSettings;

        const thumbnailsSlidesToShow =
            thumbnailsCarouselConfigSettings && thumbnailsCarouselConfigSettings.slidesToShow
                ? thumbnailsCarouselConfigSettings.slidesToShow
                : DEFAULT_THUMBNAILS_CAROUSEL_SETTINGS.slidesToShow;

        const thumbnailSliderItems = sliderImages.map((sliderImage, key) => (
            <ThumbnailImageWrapper key={key}>
                <ProductImages
                    imgPath={sliderImage.src}
                    altText={sliderImage.alt}
                    onImageLoaded={this.onImageLoaded}
                />
            </ThumbnailImageWrapper>
        ));

        const productImagesCount = sliderItems.length;

        const thumbnailsCarouselSettings = {
            ...DEFAULT_THUMBNAILS_CAROUSEL_SETTINGS,
            ...thumbnailsCarouselConfigSettings,
            slidesToShow:
                thumbnailsSlidesToShow > productImagesCount
                    ? productImagesCount
                    : thumbnailsSlidesToShow
        };

        this.setInitialSlide(carouselRef, desktopCarouselSettings);
        this.setInitialSlide(thumbnailsCarouselRef, thumbnailsCarouselSettings);

        return (
            <Desktop minWidth={Breakpoints.desktop}>
                {() => (
                    <>
                        {carouselRef && (
                            <StyledThumbnailsSlider
                                className={'elc-carousel-thumbnails'}
                                isThumbnailsSliderVertical={isThumbnailsSliderVertical}
                            >
                                <SliderView
                                    className={'slider-view'}
                                    asNavFor={carouselRef}
                                    innerRef={setThumbnailsCarouselRef}
                                    sliderSettings={thumbnailsCarouselSettings}
                                    items={thumbnailSliderItems}
                                />
                            </StyledThumbnailsSlider>
                        )}
                        <StyledProductSlider className={'elc-carousel-products'}>
                            <SliderView
                                asNavFor={thumbnailsCarouselRef}
                                innerRef={setCarouselRef}
                                sliderSettings={desktopCarouselSettings}
                                items={sliderItems}
                            />
                        </StyledProductSlider>
                    </>
                )}
            </Desktop>
        );
    }
}
